import { AInput } from "@/admin/form/Fields.tsx";
import { campaignCreateMutation, campaignTypeMap, campaignUpdateMutation } from "@/admin/marketing/campaignsQuery.ts";
import { controllerProps } from "@/components/forms/ariaController.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { siteCurrencyAtom } from "@/config.ts";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import type { Campaigns, CampaignsType } from "@warrenio/api-spec/spec.oats.gen";
import { useAtomValue } from "jotai/react";
import type { ReactNode } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

export function AddCampaignButton({ button }: { button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditCampaignModalContent />
        </WModal>
    );
}

export function EditCampaignButton({ item, button }: { item: Campaigns; button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditCampaignModalContent item={item} />
        </WModal>
    );
}

interface UserType {
    id: CampaignsType;
    title: string;
}

interface statusType {
    id: string;
    title: string;
}

interface AddEditCampaignInputs {
    campaign_code: string;
    campaign_description: string;
    campaign_type: CampaignsType;
    credit_amount: string;
    is_active: boolean;
}

function AddEditCampaignModalContent({ item }: { item?: Campaigns }) {
    const siteCurrency = useAtomValue(siteCurrencyAtom);

    const createMutation = useStandardMutation(campaignCreateMutation);
    const updateMutation = useStandardMutation(campaignUpdateMutation);

    const isEditMode = !!item;
    const typeItems: UserType[] = Object.entries(campaignTypeMap).map(([id, title]) => ({
        id: id as CampaignsType,
        title,
    }));

    const statusItems: statusType[] = [
        { id: "active", title: "Active" },
        { id: "inactive", title: "Inactive" },
    ];

    const onSubmit: SubmitHandler<AddEditCampaignInputs> = async (inputs) => {
        const { campaign_code, campaign_description, campaign_type, is_active, credit_amount } = inputs;

        if (isEditMode) {
            await updateMutation.mutateAsync({
                body: {
                    campaign_description,
                    is_active,
                },
                id: item.id,
            });
        } else {
            await createMutation.mutateAsync({
                body: {
                    campaign_code,
                    campaign_description,
                    campaign_type,
                    credit_amount: credit_amount ? Number(credit_amount) : 0,
                    is_active,
                },
            });
        }
    };

    const form = useForm<AddEditCampaignInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            campaign_code: item?.campaign_code ?? "",
            campaign_description: item?.campaign_description ?? "",
            campaign_type: item?.campaign_type ?? "new_users",
            credit_amount: item?.credit_amount ? String(item?.credit_amount) : "",
            is_active: item?.is_active ?? true,
        },
    });

    const { control } = form;

    return (
        <WModalForm
            title={isEditMode ? "Edit Campaign" : "Add New Campaign"}
            label={isEditMode ? "Edit" : "Add"}
            form={form}
            onSubmit={onSubmit}
        >
            <Controller
                control={control}
                name="campaign_code"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        isRequired
                        isReadOnly={isEditMode}
                        autoFocus
                        headless
                        wide
                        label="Code"
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="campaign_description"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        isRequired
                        headless
                        wide
                        label="Description"
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="credit_amount"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        isRequired
                        wide
                        isReadOnly={isEditMode}
                        headless
                        label={`Credit amount (${siteCurrency})`}
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="campaign_type"
                render={({ field }) => (
                    <WTextField headless isRequired wide label="Type" onChange={undefined}>
                        <WSelect
                            isDisabled={isEditMode}
                            valueKey={field.value}
                            getKey={(item) => item.id}
                            items={typeItems}
                            aria-label="Select type"
                            getTextValue={(item) => item.title}
                            onChange={(item) => field.onChange(item.id)}
                        >
                            {({ title }) => title}
                        </WSelect>
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="is_active"
                render={({ field }) => (
                    <WTextField headless isRequired wide label="Status" onChange={undefined}>
                        <WSelect
                            valueKey={field.value ? "active" : "inactive"}
                            getKey={(item) => item.id}
                            items={statusItems}
                            aria-label="Select status"
                            getTextValue={(item) => item.title}
                            onChange={(item) => field.onChange(item.id === "active")}
                        >
                            {({ title }) => title}
                        </WSelect>
                    </WTextField>
                )}
            />
        </WModalForm>
    );
}
